/**
 * Persisted content documents.
 *
 * These schemas describe what a work *is*, never how it is displayed.
 * Do not add layout or styling fields (hero, fullWidth, gridSpan, colors, ...).
 * See docs/ARCHITECTURE.md §8 and §44 before adding any field.
 */
import { z } from "zod";

export const CURRENT_SCHEMA_VERSION = 1 as const;

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

/** Opaque identifier. Generated by the CMS; never derived from a filename. */
export const IdSchema = z.string().regex(/^[A-Za-z0-9_-]{3,64}$/, "invalid id");

/** URL-stable identifier, independent of the title. */
export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const SlugSchema = z
  .string()
  .min(1)
  .max(80)
  .regex(SLUG_PATTERN, "slug must be lowercase letters, digits and single hyphens");

export function isValidSlug(value: string): boolean {
  return SlugSchema.safeParse(value).success;
}

/** Japanese is primary and required; English is optional. */
export const LocalizedTextSchema = z.object({
  ja: z.string(),
  en: z.string().optional(),
});

/** ISO 8601 timestamp, e.g. 2026-10-04T10:32:04Z. */
const TimestampSchema = z.iso.datetime({ offset: true });

/** Calendar date at year, month or day precision: 2024 / 2024-05 / 2024-05-12. */
export const PartialDateSchema = z
  .string()
  .regex(/^\d{4}(?:-(0[1-9]|1[0-2])(?:-(0[1-9]|[12]\d|3[01]))?)?$/, "expected YYYY, YYYY-MM or YYYY-MM-DD");

const YearSchema = z.number().int().min(1900).max(2200);

// ---------------------------------------------------------------------------
// Asset
// ---------------------------------------------------------------------------

export const AssetTypeSchema = z.enum(["image", "video", "document"]);
export const VariantFormatSchema = z.enum(["avif", "webp", "jpeg", "mp4", "webm"]);

/** Storage key relative to the media root, e.g. "web/<asset-id>-1280.avif". */
const StorageKeySchema = z
  .string()
  .regex(/^(originals|web)\/[A-Za-z0-9._/-]+$/, "invalid storage key")
  .refine((key) => !key.includes(".."), "invalid storage key");

export const AssetVariantSchema = z.object({
  width: z.number().int().positive().optional(),
  format: VariantFormatSchema.optional(),
  key: StorageKeySchema.refine((key) => key.startsWith("web/"), "variants must live under web/"),
});

/**
 * A physical media file. Facts about the file itself (photographer, copyright,
 * pixel size, duration) live here; how a page uses it lives in WorkMedia.
 */
export const AssetSchema = z.object({
  schemaVersion: z.literal(CURRENT_SCHEMA_VERSION),
  id: IdSchema,
  type: AssetTypeSchema,
  originalKey: StorageKeySchema.refine((key) => key.startsWith("originals/"), "original must live under originals/"),
  /** MIME type of the original as verified after upload. */
  contentType: z.string().max(100).optional(),
  /** The uploader's file name, for display only. Never used to build storage keys. */
  originalFilename: z.string().max(255).optional(),
  variants: z.array(AssetVariantSchema).optional(),
  photographer: z.string().optional(),
  copyright: z.string().optional(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  durationSeconds: z.number().nonnegative().optional(),
  createdAt: TimestampSchema,
});

// ---------------------------------------------------------------------------
// Work
// ---------------------------------------------------------------------------

/** Semantic role of a media item. Not a layout instruction. */
export const WorkMediaKindSchema = z.enum([
  "artwork",
  "detail",
  "documentation",
  "installation",
  "video-still",
  "video",
]);

export const WorkMediaSchema = z.object({
  assetId: IdSchema,
  kind: WorkMediaKindSchema,
  featured: z.boolean().optional(),
  alt: LocalizedTextSchema.optional(),
  caption: LocalizedTextSchema.optional(),
  displayOrder: z.number().int(),
});

export const CreditSchema = z.object({
  role: LocalizedTextSchema,
  name: LocalizedTextSchema,
});

/** One concrete presentation of a work in a space. Part of the Work aggregate. */
export const InstallationSchema = z.object({
  id: IdSchema,
  exhibitionId: IdSchema.optional(),
  title: LocalizedTextSchema.optional(),
  year: YearSchema.optional(),
  venueOverride: LocalizedTextSchema.optional(),
  description: LocalizedTextSchema.optional(),
  dimensions: LocalizedTextSchema.optional(),
  media: z.array(WorkMediaSchema),
  /** Never published. */
  technicalInternal: z.string().optional(),
  displayOrder: z.number().int(),
});

export const WorkTypeSchema = z.enum(["painting", "video", "media-art", "other"]);

export const WorkDateSchema = z
  .object({
    start: YearSchema,
    end: YearSchema.optional(),
    ongoing: z.boolean().optional(),
  })
  .refine((d) => d.end === undefined || d.end >= d.start, { message: "end must not be before start", path: ["end"] })
  .refine((d) => !(d.end !== undefined && d.ongoing), { message: "a work cannot have both end and ongoing", path: ["ongoing"] });

export const WorkSchema = z.object({
  schemaVersion: z.literal(CURRENT_SCHEMA_VERSION),
  id: IdSchema,
  slug: SlugSchema,
  title: LocalizedTextSchema,
  date: WorkDateSchema,
  type: WorkTypeSchema,
  /** Markdown. Rendered and sanitized at build time; HTML is never stored. */
  concept: LocalizedTextSchema,
  medium: LocalizedTextSchema.optional(),
  dimensions: LocalizedTextSchema.optional(),
  durationSeconds: z.number().nonnegative().optional(),
  media: z.array(WorkMediaSchema),
  installations: z.array(InstallationSchema),
  credits: z.array(CreditSchema),
  technical: z
    .object({
      public: LocalizedTextSchema.optional(),
      /** Never published. */
      internal: z.string().optional(),
    })
    .optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
  version: z.number().int().nonnegative(),
});

// ---------------------------------------------------------------------------
// Exhibition
// ---------------------------------------------------------------------------

export const ExhibitionTypeSchema = z.enum(["solo", "group", "screening", "event", "other"]);

/** Shape without cross-field rules, for deriving input schemas. */
export const ExhibitionObjectSchema = z.object({
  schemaVersion: z.literal(CURRENT_SCHEMA_VERSION),
  id: IdSchema,
  slug: SlugSchema.optional(),
  title: LocalizedTextSchema,
  type: ExhibitionTypeSchema,
  venue: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
  startDate: PartialDateSchema.optional(),
  endDate: PartialDateSchema.optional(),
  url: z.url({ protocol: /^https?$/ }).optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});

export const exhibitionDatesInOrder = (e: { startDate?: string; endDate?: string }) =>
  !e.startDate || !e.endDate || e.endDate >= e.startDate;

export const ExhibitionSchema = ExhibitionObjectSchema.refine(exhibitionDatesInOrder, {
  message: "endDate must not be before startDate",
  path: ["endDate"],
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type LocalizedText = z.infer<typeof LocalizedTextSchema>;
export type Asset = z.infer<typeof AssetSchema>;
export type AssetType = z.infer<typeof AssetTypeSchema>;
export type AssetVariant = z.infer<typeof AssetVariantSchema>;
export type WorkMedia = z.infer<typeof WorkMediaSchema>;
export type WorkMediaKind = z.infer<typeof WorkMediaKindSchema>;
export type Credit = z.infer<typeof CreditSchema>;
export type Installation = z.infer<typeof InstallationSchema>;
export type WorkType = z.infer<typeof WorkTypeSchema>;
export type WorkDate = z.infer<typeof WorkDateSchema>;
export type WorkDocument = z.infer<typeof WorkSchema>;
export type ExhibitionType = z.infer<typeof ExhibitionTypeSchema>;
export type ExhibitionDocument = z.infer<typeof ExhibitionSchema>;

// ---------------------------------------------------------------------------
// Parsing helpers
// ---------------------------------------------------------------------------

export type ParseResult<T> = { ok: true; value: T } | { ok: false; errors: string[] };

function toResult<T>(result: z.ZodSafeParseResult<T>): ParseResult<T> {
  if (result.success) return { ok: true, value: result.data };
  return {
    ok: false,
    errors: result.error.issues.map((issue) => `${issue.path.join(".") || "(root)"}: ${issue.message}`),
  };
}

export const parseWork = (input: unknown) => toResult(WorkSchema.safeParse(input));
export const parseExhibition = (input: unknown) => toResult(ExhibitionSchema.safeParse(input));
export const parseAsset = (input: unknown) => toResult(AssetSchema.safeParse(input));
